import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  Field,
  FieldType,
  MemberAddeditComponent,
  MemberAddeditDialogModel,
} from '../../../brokeragency/member-addedit/member-addedit.component';
import {
  VendorApiCallLogItem,
  VendorApiCallLogListResponse,
  VendorApiCallLogResponseSummary,
  VendorProductsService,
} from '../../../../services/vendorproducts/vendorproducts.service';

const HISTORICAL_UNAVAILABLE_COPY = 'Historical provider payload details are unavailable.';
const REQUEST_NOT_RETURNED_COPY = 'Request payload was not captured for this log.';
const HEADERS_NOT_RETURNED_COPY = 'HTTP headers are not returned.';
const INITIAL_LOAD_ERROR_COPY = 'Unable to load vendor API logs. Please try again.';
const PAGINATION_LOAD_ERROR_COPY = 'Unable to load more vendor API logs. Previously loaded entries remain available.';

type VendorApiCallLogLoadError = 'initial' | 'pagination';

interface VendorApiCallLogDetailData {
  id: string;
  createdAt: string;
  user: string;
  firstName: string;
  lastName: string;
  email: string;
  userId: string;
  vendorName: string;
  productName: string;
  vendorSlug: string;
  vendorProductId: string;
  httpMethod: string;
  httpStatus: string | number;
  outcome: string;
  routeTemplate: string;
  requestPayload: unknown;
  responseSummary: VendorApiCallLogResponseSummary | string;
  headerAvailabilityMessage: string;
}

@Component({
    selector: 'app-vendor-products-api-log',
    templateUrl: './vendor-products-api-log.component.html',
    styleUrls: ['./vendor-products-api-log.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VendorProductsApiLogComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly displayedColumns = [
    'createdAt',
    'user',
    'vendorName',
    'productName',
    'method',
    'status',
    'outcome',
    'actions',
  ];

  public items: VendorApiCallLogItem[] = [];
  public searchValue = '';
  public page = 0;
  public limit = 25;
  public endOfList = false;
  public loading = false;
  public loadError: VendorApiCallLogLoadError | null = null;
  public total = 0;
  @ViewChild('scrollContainer') private scrollContainer?: ElementRef<HTMLElement>;
  private readonly searchDebounceMs = 300;
  private readonly searchValueChanges = new Subject<string>();
  private readonly searchSubscription: Subscription;

  constructor(
    private vendorProductsService: VendorProductsService,
    private dialog: MatDialog,
  ) {
    this.searchSubscription = this.searchValueChanges
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadItems(true));
  }

  ngOnInit(): void {
    this.loadItems(true);
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.loadMoreIfViewportNotFilled());
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  async loadItems(clear = false, retry = false): Promise<void> {
    if (clear) {
      this.page = 0;
      this.endOfList = false;
      this.items = [];
      this.total = 0;
      this.loadError = null;
    }

    if (this.loading || this.endOfList || (this.loadError !== null && !retry)) {
      return;
    }

    this.loading = true;
    try {
      const result: VendorApiCallLogListResponse = await this.vendorProductsService.getVendorApiCallLogs(
        this.searchValue,
        this.page,
        this.limit,
      );
      this.loadError = null;
      const nextItems = result?.items || [];
      this.total = result?.total || 0;

      if (nextItems.length === 0) {
        this.endOfList = true;
      } else {
        this.items = this.items.concat(nextItems);
        this.page += 1;
        if (nextItems.length < this.limit || this.items.length >= this.total) {
          this.endOfList = true;
        }
      }
    } catch (_error) {
      this.loadError = this.items.length === 0 ? 'initial' : 'pagination';
      console.error('Unable to load vendor API call logs');
    } finally {
      this.loading = false;
      this.loadMoreIfViewportNotFilled();
    }
  }

  retryLoad(): Promise<void> {
    return this.loadItems(this.items.length === 0, true);
  }

  get loadErrorMessage(): string {
    return this.loadError === 'pagination' ? PAGINATION_LOAD_ERROR_COPY : INITIAL_LOAD_ERROR_COPY;
  }

  queueFilter(value: string): void {
    this.searchValueChanges.next((value || '').toString());
  }

  clearSearch(): void {
    this.searchValue = '';
    this.queueFilter('');
  }

  onScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;
    if (scrollTop + offsetHeight >= scrollHeight - 150) {
      this.loadItems();
    }
  }

  private loadMoreIfViewportNotFilled(): void {
    if (this.loading || this.endOfList || this.loadError !== null) {
      return;
    }

    const container = this.scrollContainer?.nativeElement;
    if (!container) {
      return;
    }

    const hasVerticalScroll = container.scrollHeight > container.clientHeight;
    const nearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 150;

    if (!hasVerticalScroll || nearBottom) {
      this.loadItems();
    }
  }

  openDetails(item: VendorApiCallLogItem): void {
    this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(
        this.buildDetailData(item),
        this.buildDetailFields(item),
        'Vendor API Log',
        false,
        'View',
      ),
    });
  }

  getUserDisplay(item: VendorApiCallLogItem): string {
    const firstName = item?.firstName || '';
    const lastName = item?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const email = item?.email || '';

    if (fullName && email) {
      return `${fullName} (${email})`;
    }
    return fullName || email || '';
  }

  getVendor(item: VendorApiCallLogItem): string {
    return item.vendorName || item.vendorSlug || '';
  }

  getProduct(item: VendorApiCallLogItem): string {
    return item.productName || '';
  }

  getMethod(item: VendorApiCallLogItem): string {
    return item.httpMethod || '';
  }

  getStatus(item: VendorApiCallLogItem): string | number {
    return item.httpStatus ?? '';
  }

  getOutcome(item: VendorApiCallLogItem): string {
    if (item.response.availability !== 'available') return 'Unavailable';
    const labels: Record<string, string> = {
      completed: 'Completed', disabled: 'Disabled', failed: 'Failed',
      partial_success: 'Partial success', pending: 'Pending', provider_error: 'Provider error',
      queued: 'Queued', running: 'Running', success: 'Success',
      timeout: 'Timed out', validation_error: 'Validation error',
    };
    return item.response.summary.outcome === null ? 'Unknown' : labels[item.response.summary.outcome];
  }

  private buildDetailData(item: VendorApiCallLogItem): VendorApiCallLogDetailData {
    return {
      id: item.id || '',
      createdAt: item.createdAt || '',
      user: this.getUserDisplay(item),
      firstName: item.firstName || '',
      lastName: item.lastName || '',
      email: item.email || '',
      userId: item.userId || '',
      vendorName: item.vendorName || this.getVendor(item),
      productName: item.productName || this.getProduct(item),
      vendorSlug: item.vendorSlug || '',
      vendorProductId: item.vendorProductId || '',
      httpMethod: item.httpMethod || '',
      httpStatus: item.httpStatus ?? '',
      outcome: this.getOutcome(item),
      routeTemplate: item.routeTemplate || '',
      requestPayload: item.requestAvailability === 'available' ? item.requestPayload : REQUEST_NOT_RETURNED_COPY,
      responseSummary: item.response.availability === 'available'
        ? item.response.summary
        : HISTORICAL_UNAVAILABLE_COPY,
      headerAvailabilityMessage: HEADERS_NOT_RETURNED_COPY,
    };
  }

  private buildDetailFields(item: VendorApiCallLogItem): Field[] {
    return [
      this.readOnlyField('createdAt', 'Created', FieldType.F_STRING),
      this.readOnlyField('user', 'User', FieldType.F_STRING),
      this.readOnlyField('vendorName', 'Vendor', FieldType.F_STRING),
      this.readOnlyField('productName', 'Product', FieldType.F_STRING),
      this.readOnlyField('vendorSlug', 'Vendor Slug', FieldType.F_STRING),
      this.readOnlyField('httpMethod', 'Method', FieldType.F_STRING),
      this.readOnlyField('httpStatus', 'HTTP Status', FieldType.F_STRING),
      this.readOnlyField('outcome', 'Outcome', FieldType.F_STRING),
      this.readOnlyField('routeTemplate', 'Route Template', FieldType.F_STRING),
      this.readOnlyField('id', 'Log ID', FieldType.F_STRING),
      this.readOnlyField('userId', 'User ID', FieldType.F_STRING),
      this.readOnlyField('vendorProductId', 'Vendor Product ID', FieldType.F_STRING),
      this.readOnlyField(
        'requestPayload',
        'Request Payload (sensitive fields redacted)',
        item.requestAvailability === 'available' ? FieldType.F_JSON : FieldType.F_STRING,
      ),
      this.readOnlyField(
        'responseSummary',
        'Response Summary',
        item.response.availability === 'available' ? FieldType.F_JSON : FieldType.F_STRING,
      ),
      this.readOnlyField('headerAvailabilityMessage', 'HTTP Headers', FieldType.F_STRING),
    ];
  }

  private readOnlyField(name: string, label: string, fieldType: FieldType): Field {
    return { name, label, fieldType, readOnly: true };
  }
}
